import { router, Link } from "@inertiajs/react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { ChevronsUpDown, GitBranch, List } from "lucide-react";
import ModuleAvatar from "@/components/ModuleAvatar";

interface QueryState {
  q?: string;
  filters?: Array<{ field: string; operator: string; value: string }>;
  sort?: string;
  direction?: "asc" | "desc";
  tree?: "expanded" | "collapsed";
}

interface DeskSubnavProps {
  mode: "index" | "show";
  label: string;
  recordLabel?: string;
  module?: { label: string; icon?: string; color?: string; path?: string };
  views?: string[];
  currentView?: string;
  query?: QueryState;
  paths: {
    index?: string;
    import?: string;
    export?: string;
    bulk_destroy?: string;
  };
  selectedIds: Array<string | number>;
  onClearSelection: () => void;
}

export default function DeskSubnav({
  mode,
  label,
  recordLabel,
  module,
  views,
  currentView,
  query,
  paths,
  selectedIds,
  onClearSelection,
}: DeskSubnavProps) {
  const [file, setFile] = useState<File | null>(null);
  const resolvedViews = views?.length ? views : undefined;
  const viewLabels: Record<string, string> = { list: "قائمة", tree: "شجرة", table: "قائمة" };
  const viewIcon = (view: string) => {
    switch (view) {
      case "tree":
        return <GitBranch className="h-4 w-4" />;
      case "table":
      case "list":
      default:
        return <List className="h-4 w-4" />;
    }
  };
  const currentViewLabel = viewLabels[currentView ?? "list"] || currentView || "قائمة";
  const queryParams = query || {};
  const isTreeView = currentView === "tree";
  const treeState = query?.tree === "expanded";

  const switchView = (nextView: string) => {
    if (!paths.index) return;
    onClearSelection();
    const params = { ...queryParams, view: nextView };
    if (nextView !== "tree") {
      delete (params as { tree?: string }).tree;
    }
    router.get(paths.index, params, { replace: true, preserveState: true });
  };

  const toggleTree = () => {
    if (!paths.index) return;
    router.get(
      paths.index,
      { ...queryParams, view: "tree", tree: treeState ? "collapsed" : "expanded" },
      { replace: true, preserveState: true }
    );
  };
  return (
    <div className="border-b bg-background px-4 py-2">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <nav className="text-muted-foreground" aria-label="breadcrumb">
          <ol className="flex flex-wrap items-center gap-2">
            {module && (
              <li className="text-foreground">
                {module.path ? (
                  <Link href={module.path} className="inline-flex items-center gap-2">
                    <ModuleAvatar
                      icon={module.icon}
                      color={module.color}
                      size="sm"
                      className="text-xs"
                    />
                    <span className="hover:text-foreground">{module.label}</span>
                  </Link>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <ModuleAvatar
                      icon={module.icon}
                      color={module.color}
                      size="sm"
                      className="text-xs"
                    />
                    <span>{module.label}</span>
                  </span>
                )}
              </li>
            )}
            {module && <li className="text-muted-foreground/70">/</li>}
            <li className="text-foreground">
              {paths.index ? (
                <Link href={paths.index} className="hover:text-foreground">
                  {label}
                </Link>
              ) : (
                label
              )}
            </li>
            {mode === "show" && recordLabel && (
              <>
                <li className="text-muted-foreground/70">/</li>
                <li className="text-foreground">{recordLabel}</li>
              </>
            )}
          </ol>
        </nav>
        <div className="flex flex-wrap items-center gap-2">
          {mode === "index" && (
            <>
              {isTreeView && (
                <Button size="sm" variant="secondary" className="gap-2" onClick={toggleTree}>
                  {treeState ? "طي الكل" : "توسيع الكل"}
                  <ChevronsUpDown className="h-4 w-4" />
                </Button>
              )}
              {resolvedViews && resolvedViews.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="secondary" className="gap-2">
                      {viewIcon(currentView || "list")}
                      {currentViewLabel}
                      <ChevronsUpDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {resolvedViews.map((view) => (
                      <DropdownMenuItem
                        key={view}
                        onSelect={() => switchView(view)}
                        className={
                          currentView === view ? "font-semibold text-foreground" : undefined
                        }
                      >
                        <span className="text-muted-foreground">{viewIcon(view)}</span>
                        {viewLabels[view] || view}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {paths.import && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="secondary" size="sm">
                      استيراد
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>استيراد</AlertDialogTitle>
                      <AlertDialogDescription>
                        ارفع ملف CSV لإضافة بيانات جديدة.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-4">
                      <Input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(event) => setFile(event.target.files?.[0] || null)}
                      />
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel>إلغاء</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          if (!file || !paths.import) return;
                          router.post(paths.import, { file }, { forceFormData: true });
                        }}
                      >
                        استيراد
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {!isTreeView && selectedIds.length > 0 && paths.export && paths.bulk_destroy && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" className="gap-2">
                      الإجراءات
                      <ChevronsUpDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      onSelect={(event) => event.preventDefault()}
                      onClick={() => {
                        const searchParams = new URLSearchParams();
                        selectedIds.forEach((id) => searchParams.append("ids[]", String(id)));
                        window.location.href = `${paths.export}?${searchParams.toString()}`;
                      }}
                    >
                      تصدير المحدد
                    </DropdownMenuItem>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                          حذف المحدد
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader className="text-right">
                          <AlertDialogTitle>هل أنت متأكد؟</AlertDialogTitle>
                          <AlertDialogDescription>
                            سيتم حذف العناصر المحددة بشكل دائم.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>إلغاء</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-white hover:bg-destructive/90"
                            onClick={() => {
                              if (!paths.bulk_destroy) return;
                              router.delete(paths.bulk_destroy, { data: { ids: selectedIds } });
                              onClearSelection();
                            }}
                          >
                            حذف
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
          {mode === "show" && (
            <Button size="sm" type="submit" form="resource-form">
              حفظ
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
